import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  jsonLd?: object | object[];
}

/**
 * SEOHead — injects per-page title, meta tags (hoisted by React 19)
 * and Schema.org JSON-LD via useEffect.
 */
export default function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogType = "website",
  jsonLd,
}: SEOHeadProps) {
  const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://vidalferrerassessors.es";
  const fullCanonical = canonical ? `${siteUrl}${canonical}` : siteUrl;

  useEffect(() => {
    if (!jsonLd) return;

    const scriptId = `jsonld-${(canonical ?? "/").replace(/\//g, "-")}`;
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = scriptId;
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [jsonLd, canonical]);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={fullCanonical} />
      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </>
  );
}
